import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import * as v from 'valibot'
import { AdminShell } from '@/components/layout/admin-shell'
import { AppShell } from '@/components/layout/app-shell'
import { Toaster } from '@/components/ui/toast'
import { DEFAULT_LOCALE, isLocale } from '@/i18n/locales'
import { LocaleProvider } from '@/i18n/provider'
import { discoverSearch } from '@/lib/discover-search'
import { fetchSession, SESSION_KEY } from '@/lib/session'
import { NotFoundPage } from '@/routes/not-found'

export interface RouterContext {
  queryClient: QueryClient
}

const bool = v.optional(v.fallback(v.boolean(), false))
const str = v.optional(v.fallback(v.string(), ''))
const page = v.optional(v.fallback(v.pipe(v.number(), v.integer(), v.minValue(1)), 1))

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function RootLayout() {
    const pathname = useRouterState({ select: (state) => state.location.pathname })
    const candidate = pathname.split('/')[1]
    const locale = isLocale(candidate ?? '') ? (candidate as import('@/i18n/locales').Locale) : DEFAULT_LOCALE
    return (
      <LocaleProvider locale={locale}>
        <Outlet />
        <Toaster />
      </LocaleProvider>
    )
  },
  notFoundComponent: NotFoundPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/$locale', params: { locale: DEFAULT_LOCALE } })
  },
})

const localeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$locale',
  beforeLoad: ({ params }) => {
    if (!isLocale(params.locale)) throw redirect({ to: '/$locale', params: { locale: DEFAULT_LOCALE } })
  },
  component: Outlet,
})

const landingRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/routes/public/landing'), 'LandingPage'),
})

const loginRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'login',
  validateSearch: v.object({
    tab: v.optional(v.fallback(v.picklist(['login', 'register']), 'login')),
    redirect: str,
  }),
  component: lazyRouteComponent(() => import('@/routes/auth/login'), 'LoginPage'),
})
const forgotRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'forgot-password',
  component: lazyRouteComponent(() => import('@/routes/auth/forgot'), 'ForgotPasswordPage'),
})
const resetRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'reset-password',
  validateSearch: v.object({ token: str }),
  component: lazyRouteComponent(() => import('@/routes/auth/reset'), 'ResetPasswordPage'),
})
const verifyRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'verify-email',
  validateSearch: v.object({ token: str }),
  component: lazyRouteComponent(() => import('@/routes/auth/verify'), 'VerifyEmailPage'),
})

async function requireSession(context: RouterContext, locale: string, href: string) {
  const user = await context.queryClient.ensureQueryData({
    queryKey: SESSION_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
  })
  if (!user)
    throw redirect({ to: '/$locale/login', params: { locale }, search: { tab: 'login', redirect: href } })
  return user
}

const onboardingRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'onboarding',
  beforeLoad: ({ context, params, location }) => requireSession(context, params.locale, location.href),
  component: lazyRouteComponent(() => import('@/routes/app/onboarding'), 'OnboardingPage'),
})

const appRoute = createRoute({
  getParentRoute: () => localeRoute,
  id: '_app',
  beforeLoad: ({ context, params, location }) => requireSession(context, params.locale, location.href),
  component: AppShell,
})

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'today',
  validateSearch: v.object({ actress: str, magnets: bool, trailer: bool, unfav: bool }),
  component: lazyRouteComponent(() => import('@/routes/app/today'), 'TodayPage'),
})

const discoverRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'discover',
  validateSearch: discoverSearch,
  component: lazyRouteComponent(() => import('@/routes/app/discover-videos'), 'DiscoverVideosPage'),
})
const discoverActressesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'discover/actresses',
  validateSearch: v.object({
    q: str,
    filter: v.optional(v.fallback(v.picklist(['all', 'subscribed', 'unsubscribed']), 'all')),
  }),
  component: lazyRouteComponent(() => import('@/routes/app/discover-actresses'), 'DiscoverActressesPage'),
})
const discoverTagsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'discover/tags',
  validateSearch: v.object({ q: str }),
  component: lazyRouteComponent(() => import('@/routes/app/discover-tags'), 'DiscoverTagsPage'),
})
const libraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'library',
  validateSearch: v.object({
    tab: v.optional(v.fallback(v.picklist(['favorites', 'subscriptions']), 'favorites')),
  }),
  component: lazyRouteComponent(() => import('@/routes/app/library'), 'LibraryPage'),
})
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'search',
  validateSearch: v.object({
    q: str,
    job: str,
    jobPage: v.optional(v.fallback(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(100)), 1)),
    scope: v.optional(v.fallback(v.picklist(['all', 'videos', 'actresses', 'tags']), 'all')),
  }),
  component: lazyRouteComponent(() => import('@/routes/app/search'), 'SearchPage'),
})
const videoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'videos/$code',
  validateSearch: v.object({ job: str }),
  component: lazyRouteComponent(() => import('@/routes/app/video-detail'), 'VideoDetailPage'),
})
const actressRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'actresses/$id',
  validateSearch: v.object({ all: bool, magnets: bool, trailer: bool }),
  component: lazyRouteComponent(() => import('@/routes/app/actress-detail'), 'ActressDetailPage'),
})
const tagRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tags/$id',
  component: lazyRouteComponent(() => import('@/routes/app/tag-detail'), 'TagDetailPage'),
})
const meRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'me',
  validateSearch: v.object({
    section: v.optional(
      v.fallback(v.picklist(['overview', 'membership', 'telegram', 'cdkey', 'security']), 'overview'),
    ),
    purchase: v.optional(v.pipe(v.string(), v.regex(/^[A-Za-z0-9_-]{8,128}$/))),
    payment: v.optional(v.pipe(v.string(), v.regex(/^[A-Za-z0-9_-]{1,128}$/))),
    plan: v.optional(v.picklist(['MONTHLY', 'YEARLY', 'LIFETIME'])),
  }),
  component: lazyRouteComponent(() => import('@/routes/app/me'), 'MePage'),
})

const adminRoute = createRoute({
  getParentRoute: () => localeRoute,
  path: 'admin',
  beforeLoad: async ({ context, params, location }) => {
    const user = await requireSession(context, params.locale, location.href)
    if (user.role !== 'ADMIN') throw redirect({ to: '/$locale/today', params: { locale: params.locale } })
  },
  component: AdminShell,
})
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/routes/admin/overview'), 'AdminOverviewPage'),
})
const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'users',
  validateSearch: v.object({
    page,
    q: str,
    status: v.optional(v.fallback(v.picklist(['', 'ACTIVE', 'FREE', 'EXPIRED', 'ADMIN']), '')),
  }),
  component: lazyRouteComponent(() => import('@/routes/admin/users'), 'AdminUsersPage'),
})
const adminCrawlerRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'crawler',
  component: lazyRouteComponent(() => import('@/routes/admin/crawler'), 'AdminCrawlerPage'),
})

const adminOrdersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'orders',
  component: lazyRouteComponent(() => import('@/routes/admin/orders'), 'AdminOrdersPage'),
})
const adminConfigRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'config',
  component: lazyRouteComponent(() => import('@/routes/admin/config'), 'AdminConfigPage'),
})
const adminContentRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'content',
  component: lazyRouteComponent(() => import('@/routes/admin/content'), 'AdminContentPage'),
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  localeRoute.addChildren([
    landingRoute,
    loginRoute,
    forgotRoute,
    resetRoute,
    verifyRoute,
    onboardingRoute,
    appRoute.addChildren([
      todayRoute,
      discoverRoute,
      discoverActressesRoute,
      discoverTagsRoute,
      libraryRoute,
      searchRoute,
      videoRoute,
      actressRoute,
      tagRoute,
      meRoute,
    ]),
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminCrawlerRoute,
      adminOrdersRoute,
      adminConfigRoute,
      adminContentRoute,
    ]),
  ]),
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
